/**
 * Site A — Kettle Narrows Dam (§2.1).
 *
 * Storage project, high head, four Francis units, a gated chute spillway on a sill 15 m below full
 * pool, and a flood-control obligation to Ferrisburg 42 km downstream. Every number here comes from
 * Section 2.1 of the brief; the two that were adjusted are `orificeCoeff` (DECISIONS D11) and the
 * reading of `ratedMW` as a cap rather than the rated-point output (DECISIONS D4).
 *
 * A site is **config, never code**. Nothing in `src/engine/` branches on this id.
 */

import type { SiteConfig } from '../engine/types';

const G = 9.81;
const C_FREE = 2.15;

export const kettleNarrows: SiteConfig = {
  id: 'kettle-narrows',
  name: 'Kettle Narrows Dam',
  river: 'Sable River',
  blurb:
    'Concrete-faced rockfill, 185 m high, four Francis units and a gated chute spillway. ' +
    'Twenty-one thousand square kilometres of snow upstream and a town forty-two kilometres down.',

  dam: { crest: 762.0, parapet: 763.5, foundation: 577.0, fsl: 754.0, mfl: 758.0, mol: 707.0 },

  /**
   * Cumulative storage above the 690 m datum, m³.
   *
   * §2.1 gives the eight knots as live storage above MOL and notes "dead storage below" 707 m.
   * The table is extended down to 690 m with 0.22 × 10⁹ m³ of dead storage and every knot shifted
   * by that amount, which preserves the stated live volume (754 − 707 = 2.50 × 10⁹ m³) and
   * surcharge (758 − 754 = 0.35 × 10⁹ m³) exactly.
   *
   * This is not bookkeeping. Without dead storage the curve bottoms out *at* MOL, so an operator
   * who runs the pool down touches the floor of the table, the mass balance clamps, and the engine
   * raises a fault — for something that is an operational hazard, not an engine error. With it,
   * hitting MOL does what it should: the below-MOL alarm, $100k/h, and units tripping on intake
   * vortex.
   */
  stageStorage: {
    h: [690, 700, 707, 715, 725, 735, 745, 754, 758, 762],
    v: [0, 0.09e9, 0.22e9, 0.54e9, 1.0e9, 1.52e9, 2.1e9, 2.72e9, 3.07e9, 3.44e9],
  },

  spillway: {
    kind: 'gated',
    sill: 739.0,
    gateCount: 4,
    gateWidth: 11.0,
    gateHeight: 16.0,
    dischargeCoeff: C_FREE,
    // C/√g — makes the free/orifice transition exactly continuous (DECISIONS D11).
    orificeCoeff: C_FREE / Math.sqrt(G),
    overtopCoeff: 1.7,
    hoistRate: 0.3,
    minNonVibrating: 0.3,
    adjacencyLimit: 1.0,
    // Centre first: 2, 3, 1, 4 in operator numbering (§2.1).
    sequence: [1, 2, 0, 3],
    scourFlow: 1200,
    scourGateHours: 24,
    heaterPreheatMinutes: 120,
    iceThresholdC: -5,
    damagedGateFlowCap: 400,
  },

  llo: {
    count: 2,
    width: 3.0,
    height: 3.0,
    invert: 640.0,
    centreline: 641.5,
    cd: 0.8,
    travelRate: 0.5,
    tdgPercent: 125,
    cavitationMinFrac: 0.1,
    cavitationMaxFrac: 0.6,
    cavitationHwThreshold: 720,
    cavitationMinutes: 120,
  },

  powerhouse: {
    count: 4,
    ratedMW: 300,
    ratedHeadM: 170,
    ratedFlow: 193,
    maxFlow: 205,
    minLoadFrac: 0.35,
    roughZoneLo: 0.2,
    roughZoneHi: 0.35,
    roughZoneMinutes: 30,
    snlFlow: 15,
    intakeGateSeconds: 180,
    syncSeconds: 120,
    stopSeconds: 180,
    startCost: 1500,
    tripHoldMinutes: 10,
    genEfficiency: 0.985,
    stationServiceMW: 3,
    penstockK: 5.4e-5,
    mva: 315,
    rpm: 150,
    governorMWPerS: 15,
    efficiency: { kind: 'parabolic', etaPeak: 0.94, qBep: 0.85, kq: 0.55, kh: 0.9, etaMin: 0.6 },
    wicket: { linear: 0.12, exp: 1.35 },
    rackDpDerateThreshold: 1.0,
    rakeMinutes: 45,
    tdgPercent: 101,
  },

  // TW = 582.0 + 0.0021·Q^0.90 (HYDRAULICS §6.2).
  tailwater: { kind: 'power', base: 582.0, coeff: 0.0021, exp: 0.9 },

  downstream: {
    gaugeName: 'Ferrisburg',
    distanceKm: 42,
    muskingumK: 5.5,
    muskingumX: 0.2,
    localInflowBase: 40,
    localInflowName: 'Otter Creek',
    floodMinor: 3000,
    floodModerate: 3800,
    floodMajor: 4600,
  },

  licence: {
    minFlow: 150,
    minFlowWinter: 120,
    winterMonths: [12, 1, 2],
    downRampMPerH: 0.1,
    upRampMPerH: 0.3,
    tdgLimit: 110,
    tdgFloodLimit: 115,
    waiverDelayMinMinutes: 30,
    waiverDelayMaxMinutes: 90,
  },

  // Jan..Dec month-start target elevations (§2.1).
  ruleCurve: {
    monthly: [748, 742, 733, 722, 726, 740, 750, 754, 754, 753, 751, 750],
    band: 3,
  },

  grid: {
    name: 'Sable Interconnection',
    exportLimitMW: 1250,
    priceNight: 22,
    priceShoulder: 45,
    pricePeakMorning: 70,
    pricePeakEvening: 95,
    weekendFactor: 0.85,
    reserveObligationMW: 100,
    reservePayment: 6,
    reservePenalty: 60,
    agcPayment: 12,
    agcRangeMW: 50,
  },

  hydrology: {
    basinAreaKm2: 21000,
    meanInflow: 620,
    bandWeights: [0.25, 0.45, 0.3],
    bandElevations: [900, 1600, 2300],
    refElevation: 760,
    sweArealMm: 200,
    ddf: 4.0,
    lapseRate: -0.0065,
    rainOnSnowFactor: 1.5,
    snowThresholdC: 1.0,
    uhRainPeakH: 18,
    uhMeltPeakH: 30,
    uhBaseH: 54,
    baseflowK: 0.985,
    baseflowRecharge: 0.15,
    runoffCMin: 0.3,
    runoffCMax: 0.7,
    wetnessTauDays: 10,
    evapMaxMmDay: 3.5,
    evapPeakDoy: 196,
    seepageBase: 2.0,
    upstreamLagH: 12,
    runoffScale: 1.0,
  },

  tdg: { base: 105, slope: 0.35, cap: 140, degasTauH: 12 },

  lock: null,
};
